import { CostCenterFragment } from "@graphql";
import locale from "@bonliva-crm/locale";
import { isBonlivaCareCostCenter } from "@bonliva-crm/shared";
import { sortBy } from "lodash";

type Option = {
  label: string;
  value: string;
};

export function getOptions(
  allCostCenters: CostCenterFragment[],
  withEmpty = false,
  withCompanies = false
): Option[] {
  const result: Option[] = sortBy(allCostCenters, customSort)
    .map<Option>((x) => ({
      label: x.Name,
      value: x.Id
    }));

  if (withCompanies) {
    result.unshift({
      label: "Bonliva Care",
      value: "BONLIVA_CARE"
    });

    result.unshift({
      label: "Bonliva",
      value: "BONLIVA"
    });
  }

  if (withEmpty) {
    result.unshift({
      label: locale.MissingCostCenter,
      value: "EMPTY"
    });
  }

  return result;
}

// Updated onChangeCostCenters to keep special values
export function onChangeCostCenters(costCenters: string[], options: Option[]): string[] {
  if (!costCenters?.length) {
    return [];
  }

  const includesBonliva = costCenters.includes("BONLIVA");
  const includesBonlivaCare = costCenters.includes("BONLIVA_CARE");
  const includesEmpty = costCenters.includes("EMPTY");

  // Base result starts with selected special values
  const result: string[] = [];
  if (includesEmpty) result.push("EMPTY");
  if (includesBonliva) result.push("BONLIVA");
  if (includesBonlivaCare) result.push("BONLIVA_CARE");

  // If both companies are selected, add all actual cost centers
  if (includesBonliva && includesBonlivaCare) {
    const allCostCenters = options
      .filter(
        (opt) =>
          opt.value !== "BONLIVA" && opt.value !== "BONLIVA_CARE" && opt.value !== "EMPTY"
      )
      .map((opt) => opt.value);
    return [...result, ...allCostCenters];
  }

  // If only Bonliva Care is selected, add Bonliva Care cost centers
  if (includesBonlivaCare) {
    const bonlivaCareCenters = options
      .filter(
        (opt) =>
          opt.value !== "BONLIVA" &&
          opt.value !== "BONLIVA_CARE" &&
          opt.value !== "EMPTY" &&
          isBonlivaCareCostCenter(opt.value)
      )
      .map((opt) => opt.value);
    return [...result, ...bonlivaCareCenters];
  }

  // If only Bonliva is selected, add non-Bonliva Care cost centers
  if (includesBonliva) {
    const bonlivaCenters = options
      .filter(
        (opt) =>
          opt.value !== "BONLIVA" &&
          opt.value !== "BONLIVA_CARE" &&
          opt.value !== "EMPTY" &&
          !isBonlivaCareCostCenter(opt.value)
      )
      .map((opt) => opt.value);
    return [...result, ...bonlivaCenters];
  }

  // If no company is selected, return selected cost centers plus any special values
  const individualCenters = costCenters.filter(
    (id) => id !== "BONLIVA" && id !== "BONLIVA_CARE" && id !== "EMPTY"
  );
  return [...result, ...individualCenters];
}

function customSort(obj: CostCenterFragment): number {
  const parsedNumber = parseInt(obj.Name);
  return isNaN(parsedNumber) ? Infinity : parsedNumber;
}
